import React, { ReactNode } from 'react';
import styled from 'styled-components';

interface FilterListboxOptionProps {
  /** Accessible label for the option — announced by screen readers. */
  readonly ariaLabel: string;
  readonly children: ReactNode;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onSelect: () => void;
  /** Whether the option is currently in the active selection. Drives the darker background +
      heavier weight visual, plus `aria-selected` / `data-selected` attributes. */
  readonly selected: boolean;
}

/**
 * A single row inside a <FilterListbox>. Renders as a `<button role="option">` with the shared
 * hover / focus / selected / disabled visuals used across the schedule view and the landing
 * page /search "More" dropdown. Selection state is controlled by the parent (multi-select or
 * single-select is a parent-level concern).
 */
const FilterListboxOption = ({
  ariaLabel,
  children,
  className,
  disabled,
  onSelect,
  selected,
}: FilterListboxOptionProps) => (
  <OptionButton
    aria-label={ariaLabel}
    aria-selected={selected}
    className={className}
    data-selected={selected}
    disabled={disabled}
    onClick={onSelect}
    role="option"
    type="button"
  >
    {children}
  </OptionButton>
);

export default FilterListboxOption;

const OptionButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: var(--way-colors-primaryTextColor);
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  padding: 8px 14px;
  text-align: left;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: var(--way-colors-contrastColorShades-5);
    outline: none;
  }

  &[data-selected='true'] {
    background: var(--way-colors-contrastColorShades-10);
    font-weight: 500;
  }
  &[data-selected='true']:hover:not(:disabled),
  &[data-selected='true']:focus-visible:not(:disabled) {
    background: var(--way-colors-contrastColorShades-20);
  }

  &:disabled {
    color: var(--way-colors-tertiaryTextColor);
    cursor: not-allowed;
    text-decoration: line-through;
  }
`;
